/* eslint-disable complexity */
import * as React from 'react'

import { VariantFields as TransformerVariant } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import {
  useExperiments,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import {
  OutOfStockButtonStyleInterface,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { formatPrice } from '@thg-commerce/enterprise-pricing/src'
import { Button, Price } from '@thg-commerce/gravity-elements'
import { PriceTheme } from '@thg-commerce/gravity-elements/Product/types'
import { Success } from '@thg-commerce/gravity-icons/src'
import {
  css,
  mq,
  spacing,
  styled,
  zIndex,
  ZIndexLevel,
} from '@thg-commerce/gravity-theme'

import {
  SubTitle,
  TagsRendererWrapper,
} from '../../components/ProductSummary/styles'
import { useProductPage } from '../../context'
import { ProductTagsRenderer } from '../../renderers/ProductTagsRenderer/ProductTagsRenderer'

const StyledButton = styled(Button)<{
  outOfStockPDPButtonStyle?: OutOfStockButtonStyleInterface
  isSticky?: boolean
  priceOnSticky?: boolean
  stickyWidth?: string
  showSuccessAnimation?: boolean
}>`
  width: 100%;
  white-space: nowrap;
  height: 48px;

  ${(props) =>
    props.showSuccessAnimation &&
    css`
      &:has(button:disabled) {
        border: none;
        box-shadow: none;
        background-color: transparent;
      }
    `}

  ${(props) =>
    props.isSticky &&
    !props.priceOnSticky &&
    css`
      width: ${props.stickyWidth || '50%'};
      margin: auto;
      display: block;

      ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
        width: 100%;
      }

      &:active,
      &:focus,
      &:hover,
      &:focus-within {
        display: block;
      }
    `}

  ${(props) =>
    props.isSticky &&
    props.priceOnSticky &&
    css`
      width: 167px;
      height: 44px;
    `}

  > button {
    &:disabled {
      ${(props) =>
        props?.outOfStockPDPButtonStyle?.disabled &&
        `border: ${
          props.outOfStockPDPButtonStyle.disabled.borderWidth || '1px'
        }  solid ${
          props.outOfStockPDPButtonStyle.disabled.borderColor || 'transparent'
        };
        cursor: ${props.outOfStockPDPButtonStyle.disabled.cursor || 'auto'};`}

      ${(props) =>
        props.showSuccessAnimation &&
        css`
          background-color: ${props.theme.colors.success.light};
          color: ${props.theme.colors.success.base};
          border: none;
        `}

      &:hover {
        text-decoration: ${(props) =>
          props.outOfStockPDPButtonStyle?.hover &&
          props.outOfStockPDPButtonStyle.hover.textDecoration};
      }
    }
  }
`

const SuccessIconWrapper = styled.div`
  margin-right: 12px;
  display: inline-flex;
  align-items: center;
`

const Container = styled.div<{
  isSticky?: boolean
  priceOnSticky?: boolean
}>`
  width: 100%;

  ${(props) =>
    props.isSticky &&
    css`
      ${zIndex(ZIndexLevel.Base5)}
      position: fixed;
      bottom: 0;
      left: 0;
      padding: ${spacing(1)} ${spacing(2)};
      border-top: 1px solid ${(props) => props.theme.colors.palette.greys.light};
      background-color: ${(props) => props.theme.colors.palette.greys.white};
    `}
  ${(props) =>
    props.priceOnSticky &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
    `}
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    position: relative;
    padding: 0;
    ${zIndex(ZIndexLevel.Base)}
  }
`

interface AddToBasketProps {
  selectedVariant?: TransformerVariant
  pricing?: {
    price: string
    rrp: string
    saving?: string
  }
  maxQuantity: number
  selectedQuantity: number
  addToBasketText: string
  addedToBasketText?: string
  forwardedRef?: React.Ref<any>
  addProductToBasketHandler?: (
    event?: React.MouseEvent<Element, MouseEvent>,
  ) => void
  disabled?: boolean
  enablePreselectedProductVariant?: boolean
  isSticky?: boolean
  stickyWidth?: boolean
  outOfStockPDPButtonStyle?: OutOfStockButtonStyleInterface
  priceOnSticky?: boolean
  priceTheme?: PriceTheme
  saveLabel?: string
  isPersonalisedProduct?: boolean
  offLabel?: string
  showTagAndSubtitleBelowCta?: boolean
  defaultCurrency?: string
  defaultLocale?: string
  showSuccessAnimation?: boolean
}

const getSubtitle = (product, selectedVariant) =>
  product?.subtitle ||
  (selectedVariant?.choices &&
    Object.values(selectedVariant.choices).length > 0 &&
    Object.values(selectedVariant.choices)
      .map((choice) =>
        typeof choice === 'object' && choice !== null
          ? choice?.displayValue ||
            choice?.label ||
            choice?.name ||
            String(choice)
          : String(choice),
      )
      .filter(Boolean)
      .join(', ')) ||
  ''

const StickyPriceDisplay = ({
  isSticky,
  priceOnSticky,
  pricing,
  priceTheme,
  saveLabel,
  offLabel,
}) => {
  if (!isSticky || !priceOnSticky) {
    return null
  }

  return (
    <Price
      priceTheme={priceTheme}
      pricing={pricing}
      savings={{ show: true }}
      savingsPercentage={{ show: true }}
      i18nText={{ saveLabel, offLabel }}
      isSticky={isSticky}
    />
  )
}

const MobileTagsAndSubtitle = ({
  isMobile,
  showTagAndSubtitleBelowCta,
  theme,
  subtitle,
}) => {
  if (!isMobile || !showTagAndSubtitleBelowCta) {
    return null
  }

  return (
    <React.Fragment>
      {subtitle && (
        <SubTitle
          textStyle={theme?.pageTheme?.productSummary?.subtitle?.textStyle}
          content={subtitle}
          order={theme.pageTheme?.productSummary?.subtitle?.order}
        />
      )}
      <TagsRendererWrapper order={theme.pageTheme?.productSummary?.tags?.order}>
        <ProductTagsRenderer />
      </TagsRendererWrapper>
    </React.Fragment>
  )
}

export const AddToBasketButton = (props: AddToBasketProps) => {
  const { defaultCurrency, defaultLocale } = useSiteDefinition()
  const { enableCTAAnimationOnPDP } = useSiteConfig()
  const theme = useTheme()
  const [isMobile, setIsMobile] = React.useState(false)
  const { product, selectedVariant } = useProductPage()

  const experiments = useExperiments()

  const renderATBMiniBasketExperimentV2 =
    enableCTAAnimationOnPDP ||
    experiments['addToBagExperiment'] === 'v2|show_mini_basket_on_ATB'

  const subtitle = React.useMemo(() => getSubtitle(product, selectedVariant), [
    product,
    selectedVariant,
  ])

  const pricing = React.useMemo(() => {
    if (!props.pricing) {
      return { price: '0.00', rrp: '0.00', saving: '0.00' }
    }

    return {
      ...props.pricing,
      saving: props.selectedVariant?.price?.saving
        ? formatPrice(
            defaultCurrency,
            props.selectedVariant.price?.saving,
            defaultLocale,
          )
        : undefined,
    }
  }, [props.pricing, props.selectedVariant, defaultCurrency, defaultLocale])

  React.useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkIfMobile()
    window.addEventListener('resize', checkIfMobile)
    return () => window.removeEventListener('resize', checkIfMobile)
  }, [])

  const isButtonDisabled =
    (props.enablePreselectedProductVariant &&
      (!props.selectedVariant?.inStock || !props.selectedVariant)) ||
    props.maxQuantity < 1 ||
    props.disabled

  return (
    <Container isSticky={props.isSticky} priceOnSticky={props.priceOnSticky}>
      <StickyPriceDisplay
        isSticky={props.isSticky}
        priceOnSticky={props.priceOnSticky}
        pricing={pricing}
        priceTheme={props.priceTheme}
        saveLabel={props.saveLabel}
        offLabel={props.offLabel}
      />

      <StyledButton
        isSticky={props.isSticky}
        priceOnSticky={props.priceOnSticky}
        stickyWidth={
          theme.pageTheme.addToBasket?.addToBasketButton?.sticky?.width
        }
        data-testid="add-to-basket-button"
        sizing="regular"
        emphasis={
          theme.pageTheme?.addToBasket?.addToBasketButton?.emphasis || 'high'
        }
        ref={props.forwardedRef}
        disabled={isButtonDisabled}
        onClick={props.addProductToBasketHandler}
        outOfStockPDPButtonStyle={props.outOfStockPDPButtonStyle}
        showSuccessAnimation={props.showSuccessAnimation}
      >
        {renderATBMiniBasketExperimentV2 && props.showSuccessAnimation && (
          <SuccessIconWrapper>
            <Success fill={theme.colors.success.base} />
          </SuccessIconWrapper>
        )}
        {renderATBMiniBasketExperimentV2 &&
        props.showSuccessAnimation &&
        props.addedToBasketText
          ? props.addedToBasketText
          : props.addToBasketText}
      </StyledButton>

      <MobileTagsAndSubtitle
        isMobile={isMobile}
        showTagAndSubtitleBelowCta={props.showTagAndSubtitleBelowCta}
        theme={theme}
        subtitle={subtitle}
      />
    </Container>
  )
}
